import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FlamingoMessageService } from '@flamingo/service/flamingo-message.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { AuctionView } from '../../../../../../../../core/model/auction-view.model';
import {
  BidMassiveItem,
  BidMassiveResult,
  MASSIVE_BID_MAX_BATCH_SIZE,
} from '../../../../../../../../core/model/bid-massive.model';
import { BuyerAccountView } from '../../../../../../../../core/model/buyer-account-view.model';
import { BuyerFundView } from '../../../../../../../../core/model/buyer-fund-view.model';
import { VirtualBalance } from '../../../../../../../../core/model/virtual-balance.model';
import { BuyerService } from '../../../../buyer.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';

/**
 * Backend error messages, as returned per auction inside the massive bid response, mapped to the
 * translation keys used to show them. Unmapped messages are shown as they come from the backend.
 */
const BACKEND_ERROR_KEYS = new Map<string, string>([
  ['Cannot create bid, a better offer already exists', 'MASSIVE_BID.ERROR.BETTER_OFFER_EXISTS'],
  ['Cannot create bid, interest rate is above max for first offer', 'MASSIVE_BID.ERROR.RATE_ABOVE_MAX'],
  ['Invalid bid, cannot raise on same buyer', 'MASSIVE_BID.ERROR.SAME_BUYER'],
  ['Auction is sold', 'MASSIVE_BID.ERROR.AUCTION_SOLD'],
  ['Auction is not started or is expired', 'MASSIVE_BID.ERROR.AUCTION_NOT_ACTIVE'],
  ['Zero value bid', 'MASSIVE_BID.ERROR.ZERO_BID'],
  ['Cannot create bid, interest rate is below min', 'MASSIVE_BID.ERROR.RATE_BELOW_MIN'],
  ['Max bid is invalid', 'MASSIVE_BID.ERROR.MAX_BID_INVALID'],
  ['Wrong buyer for auction', 'MASSIVE_BID.ERROR.WRONG_BUYER'],
  ['Wrong transaction password', 'ERROR.WRONG_TRANSACTION_PASSWORD'],
  ['Wrong fund id', 'MASSIVE_BID.ERROR.WRONG_FUND'],
]);

@Component({
  selector: 'app-auction-bid-massive-dialog',
  templateUrl: './auction-bid-massive-dialog.component.html',
  styleUrls: ['./auction-bid-massive-dialog.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class AuctionBidMassiveDialogComponent implements OnInit {
  auctions: Array<AuctionView> = [];

  /** Rates are handled as percentages in the form and converted to fractions when submitting. */
  stepPercent = 0.025;

  massiveBidFormGroup: UntypedFormGroup;
  masterRate = new UntypedFormControl(null);

  selectedFund?: BuyerFundView;
  buyer: BuyerAccountView;
  virtualBalance: VirtualBalance;

  showPassword = false;
  isSending = false;

  results: Array<BidMassiveResult> = [];

  displayedColumns = ['auctionId', 'seller', 'debtor', 'invoiceAmount', 'downPayment', 'currentRate', 'bidRate', 'conditions'];
  resultColumns = ['auctionId', 'seller', 'invoiceNumber', 'outcome'];

  MASSIVE_BID_MAX_BATCH_SIZE = MASSIVE_BID_MAX_BATCH_SIZE;

  /** Guards against an in-flight wallet response of a previously selected fund being applied. */
  private walletRequestGeneration = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AuctionBidMassiveDialogModel,
    private _buyerService: BuyerService,
    private _dialogRef: MatDialogRef<AuctionBidMassiveDialogComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _messageService: FlamingoMessageService,
    private _translateService: TranslateService,
    private _translationLoader: FuseTranslationLoaderService,
  ) {
    this._translationLoader.loadTranslations(italian, english);
  }

  ngOnInit(): void {
    this.auctions = this.data.auctions ?? [];
    this.buyer = this._buyerService.user;
    this.selectedFund = this._buyerService.userSelectedFund;
    this.showPassword = this.buyer.hasTransactionPassword;
    this.updateVirtualBalance();

    this.massiveBidFormGroup = this._formBuilder.group({
      transactionPassword: new UntypedFormControl(null, this.showPassword ? [Validators.required] : []),
      tos_1: new UntypedFormControl(false, Validators.pattern('true')),
      tos_2: new UntypedFormControl(false, Validators.pattern('true')),
      tos_3: new UntypedFormControl(false, Validators.pattern('true')),
      tos_4: new UntypedFormControl(false, Validators.pattern('true')),
      rates: this._formBuilder.array(this.auctions.map(auction => this.buildRateGroup(auction))),
    });
  }

  get rates(): UntypedFormArray {
    return this.massiveBidFormGroup.get('rates') as UntypedFormArray;
  }

  get hasResults(): boolean {
    return this.results.length > 0;
  }

  get successCount(): number {
    return this.results.filter(r => r.success).length;
  }

  rateGroup(auctionId: number): UntypedFormGroup {
    return this.rates.controls.find(
      control => control.get('auctionId').value === auctionId,
    ) as UntypedFormGroup;
  }

  /**
   * Highest rate accepted for an auction: one step below the best offer received so far, or the
   * nominal maximum when nobody has bid yet. Same rule as the single bid dialog.
   */
  maxRatePercent(auction: AuctionView): number {
    const maxRate = auction.maxBidInterestRate
      ? auction.maxBidInterestRate - this.stepPercent / 100
      : auction.nominalMaxInterestRate;

    return this.toPercent(maxRate);
  }

  downPaymentAmount(auction: AuctionView): number {
    return auction.nominalDownPaymentAmount ?? auction.downPaymentAmount ?? 0;
  }

  get totalDownPayment(): number {
    return this.auctions.reduce((total, auction) => total + this.downPaymentAmount(auction), 0);
  }

  get hasInsufficientLiquidity(): boolean {
    if (!this.virtualBalance) {
      return false;
    }

    return this.virtualBalance.virtualBalance < this.totalDownPayment;
  }

  /** Copies the rate typed once into every auction whose rate can still be edited. */
  applyMasterRate(): void {
    const value = this.masterRate.value;

    if (value === null || value === '') {
      return;
    }

    this.rates.controls
      .filter(control => control.get('bidInterestRate').enabled)
      .forEach(control => control.get('bidInterestRate').setValue(value));
  }

  selectFund(fund: BuyerFundView): void {
    this.selectedFund = fund;
    this.updateVirtualBalance();
  }

  downloadConditions(auction: AuctionView): void {
    const rate = this.rateGroup(auction.id).get('bidInterestRate').value;

    this._buyerService
      .downloadAuctionConditions(auction.id, this.toFraction(rate), this.selectedFund.id)
      .subscribe();
  }

  submitMassiveBid(): void {
    if (!this.selectedFund) {
      return;
    }

    this.isSending = true;
    this.massiveBidFormGroup.disable();

    const items: Array<BidMassiveItem> = this.rates.getRawValue().map(row => ({
      auctionId: row.auctionId,
      bidInterestRate: this.toFraction(row.bidInterestRate),
    }));

    this._buyerService
      .sendMassiveBid({
        fundId: this.selectedFund.id,
        transactionPassword: this.massiveBidFormGroup.get('transactionPassword').value,
        items,
      })
      .subscribe(
        results => {
          this.isSending = false;
          this.results = results ?? [];
        },
        (err: Error) => {
          this.isSending = false;
          this.massiveBidFormGroup.enable();
          this.applyDisabledRates();
          this._messageService.showMessage(this.errorLabel(err.message));

          if (this.isWrongPasswordError(err.message)) {
            this.massiveBidFormGroup.get('transactionPassword').setErrors({ forbidden: true });
          }
        },
      );
  }

  auctionById(auctionId: number): AuctionView | undefined {
    return this.auctions.find(auction => auction.id === auctionId);
  }

  /** Translates a backend message when known, otherwise shows it unchanged. */
  errorLabel(message?: string): string {
    if (!message) {
      return this._translateService.instant('MASSIVE_BID.ERROR.GENERAL');
    }

    const key = BACKEND_ERROR_KEYS.get(message);

    return key ? this._translateService.instant(key) : message;
  }

  close(): void {
    this._dialogRef.close(this.successCount > 0);
  }

  private buildRateGroup(auction: AuctionView): UntypedFormGroup {
    const maxPercent = this.maxRatePercent(auction);

    const group = this._formBuilder.group({
      auctionId: new UntypedFormControl(auction.id),
      bidInterestRate: new UntypedFormControl(maxPercent, [
        Validators.required,
        Validators.max(maxPercent),
        Validators.min(0),
      ]),
    });

    // IAS compliant transfers are traded at a fixed rate, the buyer cannot change it
    if (auction.invoiceIasCompliant) {
      group.get('bidInterestRate').disable();
    }

    return group;
  }

  /** Re-disables the fixed rates after a form-wide enable(), which would otherwise unlock them. */
  private applyDisabledRates(): void {
    for (const auction of this.auctions) {
      if (auction.invoiceIasCompliant) {
        this.rateGroup(auction.id).get('bidInterestRate').disable();
      }
    }
  }

  private updateVirtualBalance(): void {
    if (!this.selectedFund) {
      return;
    }

    const generation = ++this.walletRequestGeneration;

    this._buyerService.getVirtualBalance(this.selectedFund.id).subscribe(res => {
      if (generation !== this.walletRequestGeneration) {
        return;
      }
      this.virtualBalance = res ?? undefined;
    });
  }

  private isWrongPasswordError(message?: string): boolean {
    return message === 'Wrong transaction password' || message === 'WRONG_TRANSACTION_PASSWORD';
  }

  private toPercent(rate: number): number {
    return parseFloat((rate * 100).toFixed(5));
  }

  private toFraction(percent: number): number {
    return parseFloat((percent / 100).toFixed(5));
  }
}

export interface AuctionBidMassiveDialogModel {
  auctions: Array<AuctionView>;
}
